import React, { useState, useEffect, Suspense, lazy } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Routes, Route, useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

import { useAppSelector, useAppDispatch } from 'hooks';
import { handleLogin, GuideActions, FocusAreasActions } from 'state';
import { AuthActions } from 'state/auth';
import { RolesActions } from 'state/role';
import { LayerActions } from 'state/layers';

import Error404 from 'components/Error404';
import LandingPage from 'pages/LandingPage';
import PlanPage from 'pages/PlanPage';

import { FEATURE, isFeatureEnabled, REFRESH_DELAY } from 'config';

// Lazy-loaded routes — kept off the initial bundle because the libraries
// they pull in (OpenLayers, jsPDF, MUI X) are heavy and only needed once
// a user navigates to these screens.
const GISViewer = lazy(() => import('pages/GISViewer'));
const GuidedViewer = lazy(() => import('pages/GuidedViewer'));
const UserList = lazy(() => import('pages/UserList'));
const UserInviteNew = lazy(() => import('pages/UserInviteNew'));
const UserEdit = lazy(() => import('pages/UserEdit'));
const UserSelfDetails = lazy(() => import('pages/UserSelfDetails'));
const LayerManagerPage = lazy(() => import('pages/LayerManager'));

const useStyles = makeStyles()((theme) => ({
    root: {
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
    },
    errorText: {
        padding: theme.spacing(1),
        color: theme.palette.text.primary,
        width: '100%',
    },
    error404: {
        margin: theme.spacing(2),
    },
    fixScroll: {
        height: '100vh',
        overflow: 'hidden',
    },
}));

const App = () => {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { auth, guide, roles, focusarea, layers } = useAppSelector(
        (state) => state,
    );

    const { tokens, user } = auth;

    const [loginAttempted, setLoginAttempted] = useState(tokens.object != null);

    useEffect(() => {
        if (!loginAttempted)
            handleLogin(dispatch, auth, navigate).then(() => {
                setLoginAttempted(true);
            });
    }, [dispatch, auth, user, navigate]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (auth.tokens.object)
                dispatch(AuthActions.getToken({ tokens: auth.tokens.object }));
        }, REFRESH_DELAY);
        return () => {
            clearInterval(interval);
        };
    }, [auth]);

    useEffect(() => {
        if (guide.BFMCs.status === 'idle' && auth.tokens.status === 'finished')
            dispatch(GuideActions.getBFMCs());
    }, [guide.BFMCs.status, auth.tokens.status]);

    useEffect(() => {
        if (
            guide.BFMCGroups.status === 'idle' &&
            auth.tokens.status === 'finished'
        )
            dispatch(GuideActions.getBFMCGroups());
    }, [guide.BFMCGroups.status, auth.tokens.status]);

    useEffect(() => {
        if (guide.BFMCs.object && guide.selectedBFMC) {
            dispatch(
                GuideActions.setSelectedBFMC(
                    guide.BFMCs.object.find(
                        (bfmc) => bfmc.id === guide.selectedBFMC?.id,
                    ) ?? null,
                ),
            );
        }
    }, [guide.BFMCs.object]);

    useEffect(() => {
        if (roles.roles.status === 'idle' && auth.tokens.status === 'finished')
            dispatch(RolesActions.getRoles());
    }, [roles.roles.status, auth.tokens.status]);

    useEffect(() => {
        if (
            layers.layerRestrictions.status === 'idle' &&
            auth.tokens.status === 'finished'
        )
            dispatch(LayerActions.getLayerRestrictions());
    }, [layers.layerRestrictions.status, auth.tokens.status]);

    useEffect(() => {
        // Metadata is scoped per BMC — fire once auth finishes and a BMC is
        // selected, and re-fire whenever the selected BMC changes. Dropping
        // the `status === 'idle'` guard lets us recover if the first call
        // raced the BMC selection (previously produced `/metadata/undefined`
        // and a 422 that left status permanently non-idle).
        if (auth.tokens.status === 'finished' && guide.selectedBFMC?.id != null)
            dispatch(
                LayerActions.getLayerMetadata({
                    bfmc_id: guide.selectedBFMC.id,
                }),
            );
    }, [auth.tokens.status, guide.selectedBFMC?.id]);

    useEffect(() => {
        if (
            focusarea.selection.status === 'idle' &&
            auth.tokens.status === 'finished'
        )
            dispatch(FocusAreasActions.getObjectivesAndStrategies());
    }, [focusarea.selection.status, auth.tokens.status]);

    return loginAttempted ? (
        <div className={classes.root}>
            <Suspense
                fallback={
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '100vh',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                }
            >
                <Routes>
                    {isFeatureEnabled(FEATURE.FAFT_UI) ? (
                        <Route path="/" element={<LandingPage />} />
                    ) : null}

                    <Route
                        path={isFeatureEnabled(FEATURE.FAFT_UI) ? '/plan' : '/'}
                        element={<PlanPage />}
                    />
                    <Route path="/plan/:bfmcId" element={<PlanPage />} />

                    <Route
                        path="/user/self"
                        element={
                            <UserSelfDetails />
                            // </div> /admin/users/new
                        }
                    />
                    <Route
                        path="/admin/users/new"
                        element={
                            <UserInviteNew />
                            // </div> /admin/users/new
                        }
                    />
                    <Route
                        path="/admin/users"
                        element={
                            // <div className={classes.fixScroll}>
                            <UserList />
                            // </div> /admin/users/new
                        }
                    />
                    <Route path="/admin/users/:userId" element={<UserEdit />} />
                    <Route
                        path="/layers/metadata"
                        element={<LayerManagerPage />}
                    />
                    <Route
                        path="/guided"
                        element={
                            <div className={classes.fixScroll}>
                                <GuidedViewer />
                            </div>
                        }
                    />

                    <Route
                        path="/gis"
                        element={
                            <div className={classes.fixScroll}>
                                <GISViewer />
                            </div>
                        }
                    />
                    <Route
                        element={
                            <div className={classes.error404}>
                                <Error404 link="/" />
                            </div>
                        }
                    />
                </Routes>
            </Suspense>
        </div>
    ) : (
        <></>
    );
};

export default App;
